/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: button.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Button component with multiple variants, sizes, and states
    for consistent user interactions throughout the platform.

 🔧 Notes:
    - Implements multiple button variants (default, destructive, outline, etc.)
    - Supports different sizes and loading states
    - Integrates with Tailwind CSS for consistent styling
    - Provides accessibility features and keyboard navigation
    - Supports custom className and children props

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { ReactNode, ButtonHTMLAttributes } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: 'default' | 'outline' | 'ghost' | 'destructive' | 'secondary';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  className?: string;
}

export function Button({ 
  children, 
  variant = 'default', 
  size = 'md', 
  className = '', 
  disabled = false,
  ...props 
}: ButtonProps) {
  const baseClasses = 'inline-flex items-center justify-center rounded-lg font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none disabled:hover:scale-100 focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';
  
  const variantClasses = {
    default: 'bg-primary bg-gradient-to-br from-black/30 to-white/10 text-white border-0 hover:scale-105 hover:shadow-lg focus:ring-primary',
    outline: 'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground focus:ring-primary',
    ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground focus:ring-primary',
    destructive: 'bg-destructive bg-gradient-to-br from-black/30 to-white/10 text-destructive-foreground hover:scale-105 hover:shadow-lg focus:ring-destructive',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 focus:ring-secondary'
  };
  
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
    icon: 'h-9 w-9 p-0'
  };
  
  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
}
